import { CourseEnrolmentState, StudentState } from './enums';

const listAttributes = [
  'studentId',
  'firstName',
  'lastName',
  'gradYear',
  'disabled',
  'profileIcon',
  'attendanceType',
] as const;

const teacherListAttributes = [...listAttributes, 'schoolId', 'schoolLabel', 'state'] as const;

const adminListAttributes = [
  ...teacherListAttributes,
  'firstName',
  'state',
  'disabled',
  'email',
  'phoneNumber',
  'parentEmail',
  'parentPhoneNumber',
  'slackId',
  'profileIcon',
  'attendanceType',
  'requiresInvoice',
  'requiresRefund',
  'courseStates',
  'waitlistedClasses',
  'mostConsecutiveAbsences',
] as const;

// The row the exam analytics student picker is built from: who a teacher may already
// see in a list, and nothing that identifies them beyond it
const examAnalyticsListAttributes = ['gradYear', 'schoolLabel', 'state', 'profileIcon'] as const;

const minGetAttributes = [...listAttributes];

const teacherGetAttributes = [...teacherListAttributes] as const;

const getAttributes = [
  ...listAttributes,
  'state',
  'email',
  'phoneNumber',
  'settings',
  'schoolId',
  'schoolLabel',
  'birthday',
  'parentFirstName',
  'parentLastName',
  'parentEmail',
  'parentPhoneNumber',
  'streetAddress',
  'country',
  'postcode',
  'slackId',
  'xeroId',
  'slackChannels',
  'classes',
  'classCount',
  'tutorials',
  'courseStates',
  'invoiceInstallmentCount',
  'discounts',
  'requiresInvoice',
  'requiresRefund',
  'lessonsCompelted',
  'waitlistedClasses',
] as const;

const createAttributes = [
  'firstName',
  'lastName',
  'gradYear',
  'schoolId',
  'email',
  'phoneNumber',
  'parentFirstName',
  'parentLastName',
  'parentEmail',
  'parentPhoneNumber',
  'streetAddress',
  'country',
  'postcode',
] as const;

// `schoolLabel` is derived, never accepted from a caller: the students entity resolves
// it from `schoolId` and injects it before encoding, so it must survive this mask
const updateAttributes = [
  'firstName',
  'lastName',
  'schoolId',
  'schoolLabel',
  'gradYear',
  'birthday',
  'slackId',
  'xeroId',
  'email',
  'phoneNumber',
  'parentFirstName',
  'parentLastName',
  'parentEmail',
  'parentPhoneNumber',
  'streetAddress',
  'country',
  'postcode',
  'discounts',
  'attendanceType',
  'invoiceInstallmentCount',
  'state',
  'firstLessonReminded',
] as const;

const searchAttributes = [
  'gradYear',
  'firstName',
  'lastName',
  'parentFirstName',
  'parentLastName',
  'email',
  'phoneNumber',
  'parentEmail',
  'parentPhoneNumber',
  'schoolLabel',
] as const;

export const StudentAttributes = {
  DefaultList: listAttributes,
  TeacherList: teacherListAttributes,
  AdminList: adminListAttributes,
  ExamAnalyticsList: examAnalyticsListAttributes,
  MinGet: minGetAttributes,
  TeacherGet: teacherGetAttributes,
  Get: getAttributes,
  Create: createAttributes,
  Update: updateAttributes,
  Search: searchAttributes,
};

export const DEFAULT_INVOICE_GRACE_PERIOD_IN_DAYS = 14;

export const STUDENT_STATES: {
  [key: string]: {
    key: StudentState;
    value: StudentState;
    label: string;
  };
} = {
  [StudentState.Lead]: {
    key: StudentState.Lead,
    value: StudentState.Lead,
    label: 'Lead',
  },
  [StudentState.Enrolled]: {
    key: StudentState.Enrolled,
    value: StudentState.Enrolled,
    label: 'Enrolled',
  },
  [StudentState.PendingAttendance]: {
    key: StudentState.PendingAttendance,
    value: StudentState.PendingAttendance,
    label: 'Pending attendance',
  },
  [StudentState.OnTrial]: {
    key: StudentState.OnTrial,
    value: StudentState.OnTrial,
    label: 'On trial',
  },
  [StudentState.Active]: {
    key: StudentState.Active,
    value: StudentState.Active,
    label: 'Active',
  },
  [StudentState.Graduated]: {
    key: StudentState.Graduated,
    value: StudentState.Graduated,
    label: 'Graduated',
  },
  [StudentState.Archived]: {
    key: StudentState.Archived,
    value: StudentState.Archived,
    label: 'Archived',
  },
};

export const STUDENT_STATE_TRANSITIONS: { [key: string]: string[] } = {
  [StudentState.Lead]: [StudentState.Lead, StudentState.Enrolled, StudentState.Archived],
  [StudentState.Enrolled]: [StudentState.Enrolled, StudentState.Lead, StudentState.Archived],
  [StudentState.PendingAttendance]: [StudentState.PendingAttendance],
  [StudentState.OnTrial]: [StudentState.OnTrial],
  [StudentState.Active]: [StudentState.Active],
  [StudentState.Graduated]: [
    StudentState.Graduated,
    StudentState.Lead,
    StudentState.Enrolled,
    StudentState.Archived,
  ],
  [StudentState.Archived]: [StudentState.Archived, StudentState.Lead, StudentState.Enrolled],
};

export const COURSE_ENROLMENT_STATES: Record<
  CourseEnrolmentState,
  {
    key: CourseEnrolmentState;
    value: CourseEnrolmentState;
    label: string;
  }
> = {
  [CourseEnrolmentState.Active]: {
    key: CourseEnrolmentState.Active,
    value: CourseEnrolmentState.Active,
    label: 'Active',
  },
  [CourseEnrolmentState.OnTrial]: {
    key: CourseEnrolmentState.OnTrial,
    value: CourseEnrolmentState.OnTrial,
    label: 'On trial',
  },
  [CourseEnrolmentState.Unenrolled]: {
    key: CourseEnrolmentState.Unenrolled,
    value: CourseEnrolmentState.Unenrolled,
    label: 'Unenrolled',
  },
};

// Multiple Subject Discounts are:
// 2 Subjects: 5%
// 3 Subjects: 8%
// 4 Subjects: 10%
export const MULTIPLE_SUBJECT_DISCOUNTS: {
  [subjectCount: number]: number;
} = {
  1: 0,
  2: 5,
  3: 8,
  4: 10,
};
